import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import Grid from '@material-ui/core/Grid'
import Input, { InputProps } from '@material-ui/core/Input'
import { useSearchBox, useHits } from 'react-instantsearch'
import React, { ChangeEvent, PropsWithChildren, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Icon } from '../icon/icon'
import { AutocompleteResults } from './AutocompleteResults'
import { SearchBarProps } from './SearchBar'
import { StyledSearchIcon, StyledSearchIconContainer } from './StyledSearchBarIcon'
import { useRouter } from 'next/router'
import { SearchProvider } from '../search/SearchProvider'

export const DesktopSearchBar: React.FC<PropsWithChildren<SearchBarProps>> = (props) => {
  return (
    <SearchProvider>
      <DesktopSearchBarContent {...props} />
    </SearchProvider>
  )
}

export const DesktopSearchBarContent: React.FC<PropsWithChildren<SearchBarProps>> = ({
  inputRef,
  onChange,
  onClear,
  onToggle,
  onSearchIcon,
  jumpTo,
  focus = false,
  sticky = false,
  ...props
}) => {
  const { query, refine } = useSearchBox()
  const { hits } = useHits()
  const [value, setValue] = useState(query)
  const router = useRouter()

  // Update local state when query changes
  useEffect(() => {
    setValue(query)
  }, [query])

  useEffect(() => {
    if (!focus) {
      // Clear search when focus is lost
      refine('')
      setValue('')
    }
  }, [focus, refine])

  // State and Ref for toggle focus
  useEffect(() => {
    if (inputRef != null && focus) {
      inputRef.current.focus()
    }
  }, [focus, inputRef])

  // clear and close input
  const onClose = (e) => {
    refine('')
    setValue('')

    if (onClear) {
      onClear()
    }

    if (onToggle) {
      onToggle()
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    setValue(newValue)
    refine(newValue)

    if (onChange) {
      onChange(e)
    }
  }

  return (
    <StyledSearchBarContainer container justifyContent="center" direction="row" {...props}>
      <Grid item>
        <StyledDesktopInputContainer as={'form'} action={'/search'} method={'get'} onSubmit={(e) => {
          if (jumpTo && value && typeof window !== 'undefined') {
            window.location.href = `/search?q=${encodeURIComponent(value.trim())}`
          }
        }}>
          <StyledDesktopInput
            inputRef={inputRef}
            $focus={focus}
            value={value}
            onChange={handleInputChange}
            tabIndex={focus ? 0 : -1}
            name={'q'}
            placeholder="Search..."
            inputProps={{ 'aria-label': 'description' }}
          />
          <StyledDesktopInputClose
            icon={faTimes}
            $focus={focus}
            onClick={onClose}
          />
          {focus && value.trim().length > 2 && (
            <AutocompleteResults setInput={setValue} hits={hits} />
          )}
        </StyledDesktopInputContainer>
      </Grid>
      <Grid item>
        <StyledSearchIconContainer aria-label="search" $sticky={sticky} onClick={onSearchIcon}>
          {
            jumpTo ? (
              <a href={`/search?q=${value}`} >
                <StyledSearchIcon $focus={focus} icon={faSearch} />
              </a>
            ) : <StyledSearchIcon $focus={focus} icon={faSearch} />
          }
        </StyledSearchIconContainer>
      </Grid>
    </StyledSearchBarContainer>
  )
}

export const StyledSearchBarContainer = styled(Grid)`
  width: auto;
  align-items: center;
`

export const StyledDesktopInputContainer = styled(Grid)`
  color: ${props => props.theme.palette.text.secondary};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const StyledDesktopInputClose = styled(Icon)<{$focus: boolean}>`
  position: absolute;
  cursor: pointer;
  display: ${props => props.$focus ? 'block' : 'none'};
  font-size: ${props => props.theme.typography.pxToRem(20)};
  right: ${props => props.theme.typography.pxToRem(8)};
  opacity: 0.7;

  ${props => props.theme.breakpoints.up('sm')} {
    right: ${props => props.theme.typography.pxToRem(10)};
  }

  &:hover {
    color: ${props => props.theme.palette.primary.main};
  }
`

export const StyledDesktopInput = styled(Input)<{$focus: boolean} & InputProps>`
  width: ${props => props.$focus ? props.theme.typography.pxToRem(560) : 0};
  margin-right: ${props => props.theme.typography.pxToRem(10)};
  font-size: ${props => props.theme.typography.pxToRem(20)};
  opacity: 0.9;
  transition: width 0.3s ease-in-out;

  ${props => props.theme.breakpoints.down('md')} {
    margin-right: ${props => props.theme.typography.pxToRem(10)};
    width: ${props => props.$focus ? props.theme.typography.pxToRem(520) : 0};
  }

  & > input {
    padding-top: ${props => props.theme.typography.pxToRem(8)};
    padding-bottom: ${props => props.theme.typography.pxToRem(16)};
  }

  &::before {
    border-color: #707070;
    opacity: 0.5;
  }

  &::after {
    content: none;
  }
`